/**
 * Responsible for doing the baseline setup for an iconify form field behavior.
 * @module iconifySetup
 */
import camelCase from 'lodash/camelCase';
import { TYPES } from './form-field-behaviors';

/**
 * Defines default configuration for iconify.
 * @type {Object}
 */
const DEFAULT_ICONIFY_OPTIONS = {
  prefix: 'iconify',
  containerClassName: 'floating-label__iconify',
  containerModifier: '',
  containerElementName: 'div',
};

/**
 * CSS className used for the container.
 * @type {string}
 */
export const CONTAINER_CLASS_NAME = 'iconify';

/**
 * CSS className for an icon element.
 * @type {string}
 */
export const ICON_CLASS_NAME = `${CONTAINER_CLASS_NAME}__icon`;

/**
 * CSS className for a visible modifier.
 * @type {string}
 */
export const VISIBLE_CLASS_NAME = `${CONTAINER_CLASS_NAME}__icon--visible`;

/**
 * CSS className for a hidden modifier.
 * @type {string}
 */
export const HIDDEN_CLASS_NAME = `${CONTAINER_CLASS_NAME}__icon--hidden`;

/**
 * Obtains all the available icons based on how the data-iconify-* attributes
 * have been configured.
 * @private
 * @param {Array} acceptedIcons - List of icons with affiliated data
 * @param {Object} dataset - Data attributes on the container
 * @param {string} prefix - Prepended name applied to the data attributes
 * @param {Object} fieldInfo - Map of elements related to the field
 * @returns {Array} All available icons based on name, text, and the unique ID
 */
const getAvailableIcons = (acceptedIcons, dataset, prefix, fieldInfo) =>
  acceptedIcons
    .map(({ name, iconId, rule, svg }) => ({
      name,
      iconId,
      rule,
      svg,
      text: dataset[camelCase(`${prefix}-${name}`)],
    }))
    .filter(({ text, rule }) => {
      if (rule && !rule(fieldInfo)) {
        return false;
      }

      return !!text;
    });

/**
 * Creates the icon container with the selected icons.
 * @private
 * @param {Object} icons - Set of icons that will be rendered
 * @param {string} containerClassName - CSS className for the icon container
 * @param {string} iconClassName - CSS className applied to a single icon
 * @param {string} hiddenClassName - CSS className used to hide an icon
 * @returns {string} String representation of rendered icons
 */
const renderIcons = ({ icons, containerClassName, iconClassName, hiddenClassName }) => `
  <div class="${containerClassName}">
    ${icons.map(({ iconId, svg, text }) => `
      <span class="${iconClassName} ${hiddenClassName} icon ${iconClassName}--${iconId}">
        ${svg(text)}
      </span>
    `).join('')}
  </div>
`;

/**
 * Append the icon container to the DOM.
 * @private
 * @param {HTMLElement} container - Contains the label/form field
 * @param {Object} icons - Set of icons that will be rendered
 * @param {string} elementName - Name of the element for the appended element
 * @param {string} className - CSS className for the appended element
 * @param {string} modifier - Optional BEM Modifier appended to the container
 * @returns {Element} The icon container
 */
export const appendIconContainer = (container, icons, elementName, className, modifier) => {
  const iconContainer = document.createElement(elementName);
  iconContainer.classList.add(className);

  if (modifier) {
    iconContainer.classList.add(`${className}--${modifier}`);
  }

  iconContainer.innerHTML = renderIcons({
    icons,
    containerClassName: CONTAINER_CLASS_NAME,
    iconClassName: ICON_CLASS_NAME,
    hiddenClassName: HIDDEN_CLASS_NAME,
  });
  container.appendChild(iconContainer);
  return iconContainer;
};

/**
 * Looks for all icon related nodes and creates an object based on the name of
 * the icon. This will allow for the consumers of this code to easily reference
 * icons based on what has been passed in by the consumers.
 * @private
 * @param {HTMLElement} iconContainer - Element with icon container
 * @param {Array} icons - Reference to passed in icons from consumer
 * @returns {Object} Contains icons keyed by icon name with icon HTML element
 */
const findIconNodes = (iconContainer, icons) =>
  icons.reduce((acc, { name, iconId }) =>
    Object.assign({}, acc, {
      [name]: iconContainer.querySelector(`.${ICON_CLASS_NAME}--${iconId}`),
    }),
    {});

/**
 * Responsible for taking a list of icons, HTML elements, and options with the
 * intention of rendering a icon contains and the icons that have been passed
 * in. This is a common piece of functionality that covers a couple of different
 * iconify form field behavior scenarios. The goal here is to ensure iconify
 * form field behaviors can focus on their functionality without having to
 * worry about doing this type of setup and expose all the available elements
 * created so they can be leveraged by the consumers.
 * @see DEFAULT_ICONIFY_OPTIONS
 * @param {Array} possibleIcons - Icons that might be available
 * @param {Array} nodes - All form field behavior related elements
 * @param {Object} options - Used for overriding any options
 * @returns {Object} HTML elements pertaining to the icons being rendered
 */
export default function setupIconify(possibleIcons, nodes, options = {}) {
  const {
    prefix,
    containerClassName: className,
    containerModifier: modifier,
    containerElementName: elementName,
  } = Object.assign({}, DEFAULT_ICONIFY_OPTIONS, options);
  const [{ node: container }] = nodes.filter(({ type }) => TYPES.CONTAINER === type);
  const [{ node: formField }] = nodes.filter(({ type }) => TYPES.FIELD === type);
  const fieldInfo = {
    formField,
    container,
  };
  const availableIcons = getAvailableIcons(
    possibleIcons,
    container.dataset,
    prefix,
    fieldInfo,
  );

  if (!availableIcons.length) {
    return {};
  }

  const iconContainer = appendIconContainer(
    container,
    availableIcons,
    elementName,
    className,
    modifier,
  );
  const iconNodes = findIconNodes(iconContainer, availableIcons);
  return { container, formField, iconContainer, iconNodes };
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/iconify-setup.js